//Proofed
import { useState } from 'react'
import { Box, Container, Grid, Typography, Stack, Chip } from '@mui/material'
import { BookDemoModal } from '../../components/BookDemoModal'
import { FancyGoldButton } from '../../components/FancyGoldButton'
import { FancyLightButton } from '../../components/FancyLightButton'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { FaqSection } from '../../components/FaqSection'
import { COLOR_SOLUTIONS, SOLUTIONS_HERO_START, SOLUTIONS_HERO_MID, DARK_800, CHECKCIRCLE_SIZE, SECTION_DARK, BG_LIGHT, NAVBAR_BG } from '../../theme'
import { WaveDivider } from '../../components/WaveDivider'
import { ChallengesSection } from '../../components/ChallengesSection'
import { SolutionStack } from '../../components/SolutionStack'
import { HowItWorksSection } from '../../components/HowItWorksSection'
import { TrustedPartnerSection } from '../../components/TrustedPartnerSection'
import { CtaSection } from '../../components/CtaSection'
import PageMeta from '../../components/PageMeta'
import { OutcomesSection } from '../../components/OutcomesSection'
import { SelfQualificationSection } from '../../components/SelfQualificationSection'
import { RelatedSolutionsSection } from '../../components/RelatedSolutionsSection'
import {
    newToGovHighlights,
    newToGovChallenges,
    newToGovSolutionStack,
    newToGovSteps,
    newToGovOutcomes,
    newToGovQualifiers,
    newToGovFaqs,
    newToGovRelated,
} from '../../common/SolutionNewToGovContent'

const C = COLOR_SOLUTIONS

export default function NewToGovernment() {
    const [demoOpen, setDemoOpen] = useState(false)

    return (
        <Box>
            <PageMeta
                title="New to Government Contracting"
                description="Entering the UK public sector for the first time? Learn how to identify the right contracts, understand what buyers expect and submit your first competitive bid with CFP's platform and expert guidance."
                canonical="/solutions/new-to-government"
            />
            {/* ── 1. Hero  light left / dark panel right ─────────────────────── */}
            <Box sx={{
                background: `linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`,
                pt: { xs: 6, md: 10 },
                pb: { xs: 6, md: 10 },
            }}>
                <Container maxWidth="lg">


                    <Grid container spacing={5} alignItems="stretch">
                        {/* Left  copy */}
                        <Grid item xs={12} md={6}>
                            <Chip label="Getting Started · New Entrant" size="small" sx={{
                                mb: 3, backgroundColor: `${C}12`, color: C,
                                border: `1px solid ${C}40`, fontWeight: 700,
                                fontSize: 18,
                            }} />
                            <Typography variant="h1" sx={{ fontSize: { xs: '36px', md: '52px' }, mb: 2, color: NAVBAR_BG, lineHeight: 1.15 }}>
                                Your Route into{' '}
                                <Box component="span" sx={{ color: C }}>UK Government Contracts</Box>
                            </Typography>
                            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 400, mb: 4, lineHeight: 1.6 }}>
                                The public sector market is worth over £300bn a year. Most new suppliers don't know where to start, or waste months targeting the wrong opportunities. We fix that!
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FancyGoldButton to="/pricing" color={C} endIcon={<ArrowForwardIcon />}>
                                    Get Started
                                </FancyGoldButton>
                                <FancyLightButton onClick={() => setDemoOpen(true)} color={C}>
                                    Book a Discovery Call
                                </FancyLightButton>
                            </Stack>
                        </Grid>

                        {/* Right  dark highlights panel */}
                        <Grid item xs={12} md={6}>
                            <Box sx={{
                                background: `linear-gradient(135deg, ${SOLUTIONS_HERO_START} 0%, ${SOLUTIONS_HERO_MID} 100%)`,
                                border: `1px solid ${C}30`,
                                borderRadius: 3,
                                p: { xs: 3, md: 4 },
                                // height: '100%',
                            }}>
                                <Typography variant="caption" sx={{
                                    color: C, textTransform: 'uppercase',
                                    letterSpacing: '1px', fontWeight: 700,
                                }}>
                                    With this solution you'll be able to
                                </Typography>
                                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                                    {newToGovHighlights.map(h => (
                                        <Box key={h} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                            <CheckCircleIcon sx={{ color: C, fontSize: CHECKCIRCLE_SIZE, mt: 0.15, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', lineHeight: 1.6 }}>{h}</Typography>
                                        </Box>
                                    ))}
                                </Stack>

                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />

            {/* ── 2. Challenges  dark ─────────────────────────────────────────── */}
            <ChallengesSection
                title="The three blockers every new entrant faces"
                subtitle="These are solvable with the right public sector strategy"
                items={newToGovChallenges}
                color={C}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 3. Solution stack  light ────────────────────────────────────── */}
            <SolutionStack
                subtitle="Three components that work together: a platform foundation, market intelligence and expert bid preparation. Each adds value independently; together they give you the fastest, safest route to a first win"
                items={newToGovSolutionStack}
                color={C}
            />

            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />
            {/* ── 4. Journey  dark (shared HowItWorksSection) ─────────────────── */}
            <HowItWorksSection
                title="Your journey to a first win"
                subtitle="Four milestones from first alert to first submission, with the right support at every stage"
                steps={newToGovSteps}
                dark={true}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            {/* ── 5. Outcomes  light ──────────────────────────────────────────── */}
            <OutcomesSection
                subtitle="After setting up Core Professional and working through the advisory components, this is the position you'll be in."
                items={newToGovOutcomes}
                color={C}
            />

            <WaveDivider top={`linear-gradient(110deg, white 0%, ${C}25 55%, ${C}45 100%)`} bottom={DARK_800} />
            {/* ── 6. Self-qualification  dark ─────────────────────────────────── */}
            <SelfQualificationSection
                items={newToGovQualifiers}
                color={C}
                onDemoClick={() => setDemoOpen(true)}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />
            {/* ── Related solutions  light ─────────────────────────────────── */}
            <RelatedSolutionsSection
                subtitle="The platform grows with you. Here's where most new entrants head next"
                items={newToGovRelated}
                color={C}
            />
            <WaveDivider top={BG_LIGHT} bottom={DARK_800} />

            {/* ── 8. FAQ  dark ────────────────────────────────────────────────── */}
            <FaqSection
                subtitle="From businesses who've been exactly where you are now"
                items={newToGovFaqs}
                color={DARK_800}
            />
            <WaveDivider top={DARK_800} bottom={BG_LIGHT} inverted />

            <TrustedPartnerSection />

            <CtaSection
                title="Ready to start winning government contracts?"
                subtitle="Book a no-obligation discovery call. We'll confirm the right starting point for your business"
                onDemoClick={() => setDemoOpen(true)}
            />

            <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
        </Box>
    )
}
